import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { VehicleProfile } from '../../../types/Vehicle';
import vehicleProfileService from '../../../services/vehicleProfileService';
import VehicleProfileList from '../components/VehicleProfileList';
import VehicleProfileForm from '../components/VehicleProfileForm';
import ServerPagination from '../../../components/ServerPagination';
import { usePermissions } from '../../../hooks/usePermissions';
import { PageHeader, ListPage, Alert, LoadErrorState } from '@/components/ui';
import { confirmAction } from '@/components/ui';
import { describeActionError } from '../../../utils/loadError';

const VehicleProfilesPage: React.FC = () => {
  const { hasPermission } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profiles, setProfiles] = useState<VehicleProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState<VehicleProfile | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });

  const canEdit = hasPermission('vehicle:profile:manage') || hasPermission('vehicle:edit');

  useEffect(() => {
    const pageFromUrl = parseInt(searchParams.get('page') || '1');
    const pageSizeFromUrl = parseInt(searchParams.get('pageSize') || '20');
    setPagination(prev => ({ ...prev, page: pageFromUrl, pageSize: pageSizeFromUrl }));
  }, [searchParams]);

  useEffect(() => {
    fetchProfiles();
  }, [pagination.page, pagination.pageSize]);

  const updateUrlParams = (newPagination?: Partial<typeof pagination>) => {
    const params = new URLSearchParams(searchParams);
    
    if (newPagination) {
      if (newPagination.page) params.set('page', newPagination.page.toString());
      if (newPagination.pageSize) params.set('pageSize', newPagination.pageSize.toString());
    }
    
    setSearchParams(params);
  };

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      setLoadFailed(false);
      const response = await vehicleProfileService.getProfilesPaginated(pagination.page, pagination.pageSize);
      setProfiles(response.data);
      setPagination(prev => ({ ...prev, ...response.pagination }));
    } catch (error) {
      console.error('Error fetching profiles:', error);
      // A failed load is not an empty list (STATE-02)
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    updateUrlParams({ page });
  };

  const handlePageSizeChange = (pageSize: number) => {
    updateUrlParams({ page: 1, pageSize });
  };

  const handleCreateProfile = () => {
    setEditingProfile(null);
    setShowProfileForm(true);
  };

  const handleEditProfile = (profile: VehicleProfile) => {
    setEditingProfile(profile);
    setShowProfileForm(true);
  };

  const handleDeleteProfile = async (profileId: number) => {
    if (!(await confirmAction({ title: 'Delete Vehicle Profile?', body: 'Vehicles using this profile will need to be reassigned.', confirmLabel: 'Delete', destructive: true }))) {
      return;
    }

    setActionError(null);
    try {
      await vehicleProfileService.deleteProfile(profileId);
      await fetchProfiles();
    } catch (error: any) {
      console.error('Error deleting profile:', error);
      setActionError(describeActionError(error, "Couldn't delete the vehicle profile. Try again."));
    }
  };

  const handleProfileFormClose = () => {
    setShowProfileForm(false);
    setEditingProfile(null);
    fetchProfiles();
  };

  return (
    <ListPage>
      <PageHeader
        title="Vehicle Profiles"
        subtitle="Manage vehicle inspection profiles. Profiles define inspection requirements and settings for different vehicle classifications."
        actions={[
          {
            key: 'add',
            label: 'Add Profile',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !canEdit,
            onClick: handleCreateProfile,
          },
        ]}
      />

      {actionError && (
        <Alert variant="danger" role="alert" dismissible onClose={() => setActionError(null)} className="tw-mb-3">
          {actionError}
        </Alert>
      )}

      {/* The list now sits in a white card (DataTable fill) instead of bare on the canvas. */}
      <ListPage.Body aria-busy={loading}>
        {loadFailed && !loading ? (
          <LoadErrorState what="vehicle profiles" onRetry={() => void fetchProfiles()} />
        ) : (
          <VehicleProfileList
            profiles={profiles}
            loading={loading}
            onEdit={handleEditProfile}
            onDelete={handleDeleteProfile}
            canEdit={canEdit}
          />
        )}
      </ListPage.Body>

      <ServerPagination
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={loading}
        className="tw-mt-2"
      />

      {showProfileForm && (
        <VehicleProfileForm
          profile={editingProfile}
          onClose={handleProfileFormClose}
        />
      )}
    </ListPage>
  );
};

export default VehicleProfilesPage;
